import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const PLACEHOLDER_TEXT = {
    en: {
        bgNotRendered: "Background not rendered",
        spriteNotRendered: "Sprite not rendered",
        unnamed: "(unnamed)",
        noMessages: "(no messages)",
        none: "(none)",
        emptyStage: "(empty stage — type / for menu)"
    },
    "zh-CN": {
        bgNotRendered: "背景未渲染",
        spriteNotRendered: "立绘未渲染",
        unnamed: "（未命名）",
        noMessages: "（暂无消息）",
        none: "（无）",
        emptyStage: "（空舞台 — 输入 / 唤出菜单）"
    }
};
function pt(key, locale) {
    return (PLACEHOLDER_TEXT[locale] ?? PLACEHOLDER_TEXT.en)[key];
}
const DASHED_BORDER = "1px dashed var(--border-strong)";
const MUTED_TEXT = "var(--text-muted)";
const STRONG_TEXT = "var(--text-primary)";
const BADGE_BG = "var(--surface-elevated)";
const SMALL_CAPS = {
    fontVariantCaps: "all-small-caps",
    letterSpacing: "0.04em"
};
// Player-parity amber-on-dark "未渲染" treatment (PlayerStage.tsx BgLayer /
// CharPortrait): a warm cream label on a faint paper-tinted card with an amber
// hairline border, over a deep slate fill. Inlined (not a shared token) to
// match the exact player values; do NOT swap in --paper / --ink (undefined in
// the Studio token set).
const AMBER_TEXT = "#f0c98a";
const AMBER_BORDER = "rgba(239, 159, 39, 0.55)";
const AMBER_CARD_BG = "rgba(250, 238, 218, 0.1)";
const MONO_FONT = "var(--font-mono)";
export function BgPlaceholder({ name, locale = "en" }) {
    // Mirrors PlayerStage BgLayer's missing-bg state: deep-slate full fill with a
    // centered amber "背景未渲染" card + the mono asset name.
    return (_jsx("div", { style: {
            alignItems: "center",
            background: "#12161f",
            display: "flex",
            inset: 0,
            justifyContent: "center",
            position: "absolute"
        }, children: _jsxs("div", { style: {
                alignItems: "center",
                background: AMBER_CARD_BG,
                border: `1px solid ${AMBER_BORDER}`,
                borderRadius: 12,
                color: AMBER_TEXT,
                display: "flex",
                flexDirection: "column",
                gap: 8,
                maxWidth: "78%",
                padding: "16px 18px",
                textAlign: "center"
            }, children: [_jsx("span", { style: { fontSize: 13, fontWeight: 500 }, children: pt("bgNotRendered", locale) }), name ? (_jsx("span", { style: { fontFamily: MONO_FONT, fontSize: 12, wordBreak: "break-all" }, children: name })) : null] }) }));
}
export function CharacterSilhouette({ name, lane, look, locale = "en" }) {
    // Mirrors PlayerStage CharPortrait's missing state: a dashed amber
    // "立绘未渲染 / {name} / {look}" card anchored at the bottom of the lane's
    // sprite slot. Edge-anchored per lane to track characterGeometry() in Stage.tsx
    // (left:-6% / right:-6% / center), but inset 2% so the card body stays fully
    // readable instead of bleeding off-frame like a real cropped sprite.
    const lanePos = lane === "left"
        ? { left: "2%" }
        : lane === "right"
            ? { right: "2%" }
            : { left: "50%", transform: "translateX(-50%)" };
    return (_jsx("div", { style: {
            alignItems: "center",
            bottom: "18%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            pointerEvents: "none",
            position: "absolute",
            width: "44%",
            ...lanePos
        }, children: _jsxs("div", { style: {
                alignItems: "center",
                background: AMBER_CARD_BG,
                border: `1px dashed ${AMBER_BORDER}`,
                borderRadius: 12,
                color: AMBER_TEXT,
                display: "flex",
                flexDirection: "column",
                gap: 6,
                maxWidth: "100%",
                padding: "12px 14px",
                textAlign: "center"
            }, children: [_jsx("span", { style: { fontSize: 12, fontWeight: 500 }, children: pt("spriteNotRendered", locale) }), _jsx("span", { style: { fontFamily: MONO_FONT, fontSize: 11, wordBreak: "break-all" }, children: look ? `${name} / ${look}` : name })] }) }));
}
export function CgPlaceholder({ name, locale = "en" }) {
    return (_jsx("div", { style: {
            alignItems: "center",
            border: DASHED_BORDER,
            borderRadius: "var(--r-4)",
            color: MUTED_TEXT,
            display: "flex",
            fontFamily: "var(--font-mono)",
            fontSize: "var(--fz-4)",
            inset: "12% 8%",
            justifyContent: "center",
            position: "absolute"
        }, children: _jsxs("span", { style: SMALL_CAPS, children: ["cg: ", name || pt("unnamed", locale)] }) }));
}
export function PhonePlaceholder({ phone, locale = "en" }) {
    return (_jsx("div", { style: {
            background: "var(--scrim-soft)",
            border: DASHED_BORDER,
            borderRadius: "var(--r-4)",
            bottom: "20%",
            color: STRONG_TEXT,
            display: "flex",
            flexDirection: "column",
            gap: "var(--sp-1)",
            left: "50%",
            maxHeight: "55%",
            overflow: "hidden",
            padding: "var(--sp-3)",
            position: "absolute",
            top: "12%",
            transform: "translateX(-50%)",
            width: "55%"
        }, children: phone.messages.length === 0 ? (_jsx("span", { style: { color: MUTED_TEXT, fontSize: "var(--fz-2)", textAlign: "center" }, children: pt("noMessages", locale) })) : (phone.messages.map((message, idx) => (_jsxs("div", { style: {
                alignSelf: message.direction === "from" ? "flex-start" : "flex-end",
                background: message.direction === "from" ? "var(--border-subtle)" : "var(--bubble-accent)",
                borderRadius: "var(--r-3)",
                fontSize: "var(--fz-2)",
                maxWidth: "85%",
                padding: "6px 10px"
            }, children: [_jsxs("div", { style: { color: MUTED_TEXT, fontSize: "var(--fz-1)", ...SMALL_CAPS }, children: [message.direction, " \u00B7 ", message.speaker || "?"] }), _jsx("div", { children: message.text })] }, idx)))) }));
}
export function MusicBadge({ name, state, locale = "en" }) {
    return (_jsx("div", { style: {
            background: BADGE_BG,
            borderRadius: 999,
            // Top-left: bubble overlay claims top-right, dialogue box claims
            // the full bottom strip. Keeping music/sfx out of the dialogue
            // overlap area avoids the readability collision seen in the
            // monitor stage when a long narrator line lands during fadeout.
            top: "var(--sp-3)",
            color: STRONG_TEXT,
            fontFamily: "var(--font-mono)",
            fontSize: "var(--fz-1)",
            left: "var(--sp-3)",
            padding: "4px 10px",
            position: "absolute"
        }, children: _jsxs("span", { style: SMALL_CAPS, children: ["\u266A ", name || pt("none", locale), " \u00B7 ", state] }) }));
}
export function SfxBadge({ name }) {
    return (_jsx("div", { style: {
            background: BADGE_BG,
            borderRadius: 999,
            // See MusicBadge for the rationale on the top-left placement.
            // Sfx and music are rendered mutually-exclusively by Stage.tsx,
            // so they share the same slot.
            top: "var(--sp-3)",
            color: STRONG_TEXT,
            fontFamily: "var(--font-mono)",
            fontSize: "var(--fz-1)",
            left: "var(--sp-3)",
            padding: "4px 10px",
            position: "absolute"
        }, children: _jsxs("span", { style: SMALL_CAPS, children: ["! ", name] }) }));
}
export function BubbleOverlay({ bubble }) {
    return (_jsxs("div", { style: {
            background: "var(--bubble-paper)",
            borderRadius: 999,
            color: "var(--bubble-paper-text)",
            fontFamily: "var(--font-mono)",
            fontSize: "var(--fz-1)",
            padding: "4px 10px",
            position: "absolute",
            right: "var(--sp-3)",
            top: "var(--sp-3)",
            ...SMALL_CAPS
        }, children: [bubble.character || "?", " \u00B7 ", bubble.kind || "?"] }));
}
export function EmptyStage({ mode, locale = "en" }) {
    // The "type / for menu" hint is editor-only. In the player (Preview) there is
    // no slash menu and the host already shows its own empty-state copy, so render
    // nothing here to avoid a second, contradictory message.
    if (mode === "player") {
        return null;
    }
    return (_jsx("div", { style: {
            alignItems: "center",
            color: MUTED_TEXT,
            display: "flex",
            fontFamily: "var(--font-mono)",
            fontSize: "var(--fz-3)",
            inset: 0,
            justifyContent: "center",
            position: "absolute",
            textAlign: "center"
        }, children: pt("emptyStage", locale) }));
}
